import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DashboardService } from 'src/app/core/dashboard/dashboard.service';
import { HomeDataDto, RawFormUserInformation } from 'src/app/data/dtos';
import { IuserApplicationsAndJobs } from 'src/app/data/interfaces';
import { AppStateInterface } from '../types';
import * as HomeDataActions from './actions';
import * as ProfileDataActions from '../profile/actions';
import { successHomeDataSelector } from './selectors';

@Injectable()
export class HomeDataEffect {
  private readonly companyLogo: string = 'company_logo';
  private readonly jobCategories: string = 'job_categories';

  private createJobsQuery(jobIds: string[], activeLanguage: string): string {
    var jobsFilterQueryPart = '';
    for (let i = 1; i < jobIds.length + 1; i++) {
      jobsFilterQueryPart =
        jobsFilterQueryPart + `&filters[slug][$in][${i}]=${jobIds[i - 1]}`;
    }
    var appliedJobsQuery = `locale=${activeLanguage}&populate=${this.companyLogo}&populate=${this.jobCategories}`;
    appliedJobsQuery = appliedJobsQuery + jobsFilterQueryPart;
    return appliedJobsQuery;
  }

  getHomeData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeDataActions.getHomeData),
      withLatestFrom(this._store.select(successHomeDataSelector)),
      concatMap(([action, homeDataSuccess]) => {
        // if data is already fetched and the language is no changed, avoid fetching again.
        if (
          homeDataSuccess &&
          homeDataSuccess.applicationToJobMapping.length > 0 &&
          action.activeLanguage ===
            homeDataSuccess.applicationToJobMapping[0].job.attributes.locale
        ) {
          return of(HomeDataActions.getHomeDataAlreadyLoaded(homeDataSuccess));
        }
        return this._dashboardService.getHomeData().pipe(
          tap((homeData) => {
            if(homeData && homeData){
              const profileData = {
                email: homeData.email ?? undefined,
                user_information: homeData.user_information,
                documents: homeData.documents,
              }
              this._store.dispatch(ProfileDataActions.getProfileDataFromHomeSuccess(profileData))
            }

          }),
          switchMap((homeData) => {
            let newHomeData: HomeDataDto = homeData;
            if (homeData.applications && homeData.applications.length > 0) {
              const appliedJobsIds = homeData.applications.map(
                (application) => application.jobSlug,
              );
              const appliedJobsQuery = this.createJobsQuery(
                appliedJobsIds,
                action.activeLanguage,
              );
              return this._dashboardService.getJobs(appliedJobsQuery).pipe(
                map((result) => {
                  const applicationToJobMapping: IuserApplicationsAndJobs[] =
                    [];
                  if (homeData.applications) {
                    for (const application of homeData.applications) {
                      for (const job of result.data) {
                        if (job.attributes.slug === application.jobSlug) {
                          applicationToJobMapping.push({
                            job,
                            application,
                          });
                        }
                      }
                    }
                  }
                  newHomeData = {
                    ...homeData,
                    applicationToJobMapping,
                  };
                  return HomeDataActions.getHomeDataSuccess(newHomeData);
                }),
              );
            }
            // Returning an observable directly here to maintain consistency
            return of(HomeDataActions.getHomeDataSuccess(newHomeData));
          }),
          catchError((error) =>
            of(HomeDataActions.getHomeDataFailure({ error })),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private _dashboardService: DashboardService,
    private _store: Store<AppStateInterface>,
  ) {}
}
